import { translate as t } from '@thoughtspot/i18n';
import { useCallback, useState } from 'react';
import { create } from '../../../utils/logger';
import { validateFile } from './csv-validation.util';
const logger = create('Admin.ManualTranslation.Upload');
export function useTranslationUpload() {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const uploadCSV = useCallback(async (file) => {
        const validation = validateFile(file);
        if (!validation.isValid) {
            setUploadError(validation.error ||
                t('adminSection.manualTranslation.uploadFailed'));
            return { success: false, error: validation.error };
        }
        setUploading(true);
        setUploadError(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('filename', file.name);
            const response = await fetch('/prism/v1/localization/upload', {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });
            if (!response.ok) {
                try {
                    const errorData = await response.json();
                    const detail = errorData?.error || errorData?.message;
                    if (detail) {
                        logger.warn('Manual translation upload rejected by server', detail);
                    }
                    else {
                        logger.warn('Manual translation upload failed', `HTTP ${response.status}`);
                    }
                }
                catch {
                    logger.warn('Manual translation upload failed', `HTTP ${response.status}`);
                }
                const userError = t('adminSection.manualTranslation.uploadFailed');
                setUploadError(userError);
                return { success: false, error: userError };
            }
            let result;
            try {
                result = await response.json();
            }
            catch {
                result = { success: true };
            }
            if (result.success === false) {
                logger.warn('Manual translation upload rejected', result.error);
                const userError = t('adminSection.manualTranslation.uploadFailed');
                setUploadError(userError);
                return { success: false, error: userError };
            }
            setUploadError(null);
            return { success: true };
        }
        catch (error) {
            logger.warn('Manual translation upload error', error);
            const userError = t('adminSection.manualTranslation.uploadFailed');
            setUploadError(userError);
            return { success: false, error: userError };
        }
        finally {
            setUploading(false);
        }
    }, []);
    const clearUploadError = useCallback(() => {
        setUploadError(null);
    }, []);
    const [downloadingExistingCsv, setDownloadingExistingCsv] = useState(false);
    const [existingCsvDownloadError, setExistingCsvDownloadError] = useState(null);
    const checkCsvExists = useCallback(async () => {
        try {
            const response = await fetch('/prism/v1/localization/csv-status', {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                return false;
            }
            const data = await response.json();
            return Boolean(data?.exists);
        }
        catch {
            return false;
        }
    }, []);
    const downloadExistingCsv = useCallback(async () => {
        setDownloadingExistingCsv(true);
        setExistingCsvDownloadError(null);
        try {
            const response = await fetch('/prism/v1/localization/download-csv', {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'manual-translation.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
        catch (error) {
            logger.warn('Download existing CSV failed', error);
            setExistingCsvDownloadError(t('adminSection.manualTranslation.downloadCsvError'));
        }
        finally {
            setDownloadingExistingCsv(false);
        }
    }, []);
    const clearExistingCsvDownloadError = useCallback(() => {
        setExistingCsvDownloadError(null);
    }, []);
    return {
        uploadCSV,
        uploading,
        uploadError,
        clearUploadError,
        setUploadError,
        checkCsvExists,
        downloadExistingCsv,
        downloadingExistingCsv,
        existingCsvDownloadError,
        clearExistingCsvDownloadError,
    };
}
//# sourceMappingURL=translation-upload.hook.js.map